import type {
  Confidence,
  Investor,
  InvestorInstrument,
  InvestorStage,
  InvestorType,
  ValuationBasis,
} from "./api";
import { STATUS_HUE } from "./statusColors";

/**
 * Fundraising board metadata and the derived numbers the page shows.
 *
 * The backend stores raw terms only; everything computed lives here so it is
 * testable without a database — mirroring how PipelinePage rolls up deals.
 */

/** Columns in funnel order. */
export const INVESTOR_STAGE_COLUMNS: { stage: InvestorStage; label: string }[] = [
  { stage: "lead", label: "Lead" },
  { stage: "conversation", label: "Applied / In conversation" },
  { stage: "diligence", label: "Due diligence" },
  { stage: "term_sheet", label: "Term sheet" },
  { stage: "committed", label: "Committed" },
  { stage: "passed", label: "Passed" },
];

export const INVESTOR_STAGE_COLOR: Record<InvestorStage, string> = {
  lead: STATUS_HUE.neutral,
  conversation: STATUS_HUE.blue,
  diligence: STATUS_HUE.amber,
  term_sheet: STATUS_HUE.purple,
  committed: STATUS_HUE.green,
  passed: STATUS_HUE.red,
};

/** Terminal stages, excluded from live counts — as won/lost are in the pipeline. */
export const CLOSED_STAGES: InvestorStage[] = ["committed", "passed"];

export const isLive = (stage: InvestorStage) => !CLOSED_STAGES.includes(stage);

export const INVESTOR_TYPE_LABEL: Record<InvestorType, string> = {
  angel: "Angel",
  vc: "VC",
  grant: "Grant",
  accelerator: "Accelerator",
  friends_family: "Friends & family",
  debt: "Debt",
  revenue_share: "Revenue share",
};

export const INSTRUMENT_LABEL: Record<InvestorInstrument, string> = {
  equity: "Equity",
  safe: "SAFE",
  convertible: "Convertible loan note",
  grant: "Grant (non-dilutive)",
  debt: "Debt",
  revenue_share: "Revenue share",
};

/** Only these cost us equity; the rest can't sensibly carry a percentage. */
const DILUTIVE: InvestorInstrument[] = ["equity", "safe", "convertible"];

export const isDilutive = (instrument: InvestorInstrument) => DILUTIVE.includes(instrument);

export const CONFIDENCE_LABEL: Record<Confidence, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

/** How much of a live investor's headline amount to count toward the raise. */
export const CONFIDENCE_WEIGHT: Record<Confidence, number> = {
  low: 0.25,
  medium: 0.5,
  high: 0.8,
};

export interface Valuation {
  pre: number;
  post: number;
}

/**
 * What the terms imply the company is worth. Typed by hand it's a number
 * nobody checks; derived, it makes a bad deal obvious the moment it's entered.
 *
 * A percentage is normally read off the post-money company, but term sheets
 * do quote it pre-money, and the same headline % means materially different
 * money either way — hence the explicit basis.
 */
export function impliedValuation(
  amount: number | null,
  equityPct: number | null,
  basis: ValuationBasis,
  instrument: InvestorInstrument = "equity",
): Valuation | null {
  if (!isDilutive(instrument)) return null;
  if (amount == null || equityPct == null) return null;
  if (amount <= 0 || equityPct <= 0 || equityPct > 100) return null;
  const base = amount / (equityPct / 100);
  return basis === "post"
    ? { post: base, pre: base - amount }
    : { pre: base, post: base + amount };
}

export interface RaiseSummary {
  /** Money actually committed. */
  committed: number;
  /** Headline value of everything still live. */
  liveTotal: number;
  /** Live money discounted by confidence — the number worth planning against. */
  weighted: number;
  liveCount: number;
  /**
   * Equity promised across term sheets and commitments. Each conversation
   * looks reasonable alone; this is the only place the total shows up.
   */
  promisedDilution: number;
  /** Next steps whose date has passed — the reason investors go quiet. */
  overdue: number;
}

/** Stages where an equity number is a real promise rather than a guess. */
const PROMISED_STAGES: InvestorStage[] = ["term_sheet", "committed"];

export function summarise(investors: Investor[], today = isoToday()): RaiseSummary {
  const live = investors.filter((i) => isLive(i.stage));
  return {
    committed: sum(investors.filter((i) => i.stage === "committed").map((i) => i.amount ?? 0)),
    liveTotal: sum(live.map((i) => i.amount ?? 0)),
    weighted: sum(live.map((i) => (i.amount ?? 0) * CONFIDENCE_WEIGHT[i.confidence])),
    liveCount: live.length,
    promisedDilution: sum(
      investors
        .filter((i) => PROMISED_STAGES.includes(i.stage) && isDilutive(i.instrument))
        .map((i) => i.equityPct ?? 0),
    ),
    overdue: live.filter((i) => isOverdue(i.nextStepOn, today)).length,
  };
}

/** ISO dates compare correctly as strings, so no Date parsing is needed. */
export function isOverdue(date: string | null, today = isoToday()): boolean {
  return date != null && date < today;
}

/** A parked investor is waiting on a date, not dead — "come back at £30k MRR". */
export function isParked(investor: Investor, today = isoToday()): boolean {
  return (
    investor.revisitOn != null && investor.revisitOn > today && isLive(investor.stage)
  );
}

export function isoToday(): string {
  const now = new Date();
  // Local calendar date, not UTC: a deadline is "today" in the user's day.
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** "12.5%" without trailing zeros on whole numbers. */
export function fmtPct(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${Number(value.toFixed(2))}%`;
}

export function fmtDate(date: string | null): string {
  if (!date) return "—";
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`;
}

const pad = (n: number) => String(n).padStart(2, "0");
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

export { fmtMoney } from "./money";
